import { FolderKanban, Inbox, ListTodo, Plus, Search, WifiOff } from 'lucide-react';
import { type CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { endSessionInPage } from '../api/auth';
import { getOfflineStamp, subscribeOfflineStamp } from '../api/client';
import { formatDateTime } from '../lib/dates';
import { clearUserLocalState } from './local-state';
import { BUILD_ID, subscribePwa } from './pwa';
import { useSession } from './session';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useUnreadCount } from '../api/inbox';
import { useRealtime } from '../api/stream';
import type { Task } from '../api/types';
import { Counter } from '../components/ui/Badge';
import { useEscapeLayer } from '../components/ui/escape';
import { useToast } from '../components/ui/Toast';
import { CreateTaskDialog, type CreateDefaults } from '../features/tasks/CreateTaskDialog';
import { TaskActionsProvider } from '../features/tasks/TaskActions';
import { TaskPanel } from '../features/tasks/TaskPanel';
import { TransitionGuardProvider } from '../features/tasks/TransitionGuard';
import { TrainingProvider } from '../features/training/TrainingProvider';
import { cn } from '../lib/cn';
import { isTypingTarget, useIsDesktop, useLocalStorage, useOnline } from '../lib/hooks';
import { isKeyboardModality } from '../lib/inputModality';
import { CommandPalette } from './CommandPalette';
import { type ShellActions, ShellContext } from './shell-context';
import { ShortcutsDialog } from './ShortcutsDialog';
import { SidebarNav } from './Sidebar';
import { TASK_FOCUS_EVENT } from './task-nav';
import { Topbar } from './Topbar';

/** Висувне меню навігації для телефона й планшета. */
function NavDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [closing, setClosing] = useState(false);
  useEscapeLayer(open, onClose);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      setClosing(false);
      el.showModal();
    } else if (!open && el.open) {
      setClosing(true);
      // Закриття з клавіатури (Esc) чи за зменшеного руху — без анімації виходу.
      const instant = isKeyboardModality() || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const t = window.setTimeout(() => {
        el.close();
        setClosing(false);
      }, instant ? 0 : 180);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);
  return (
    <dialog
      ref={ref}
      data-ers-layer=""
      aria-label="Навігація"
      data-closing={closing ? '' : undefined}
      onCancel={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        if (e.target === ref.current) {
          const r = ref.current.getBoundingClientRect();
          if (e.clientX > r.right) onClose();
        }
      }}
      className="ers-chrome ers-sheet-left fixed inset-y-0 left-0 m-0 h-dvh max-h-none w-[min(300px,86vw)] border-r border-line bg-canvas p-0 text-fg pt-[env(safe-area-inset-top)]"
    >
      {open && <SidebarNav onNavigate={onClose} />}
    </dialog>
  );
}

function BottomNav({ onCreate }: { onCreate: () => void }) {
  const unread = useUnreadCount();
  const item = 'flex flex-1 flex-col items-center justify-center gap-0.5 text-caption';
  const cls = ({ isActive }: { isActive: boolean }) => cn(item, isActive ? 'font-medium text-accent-fg' : 'text-fg-3');
  return (
    <nav
      id="app-bottomnav"
      aria-label="Швидка навігація"
      className="fixed inset-x-0 bottom-0 z-30 flex h-[calc(56px+env(safe-area-inset-bottom))] items-stretch border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] sm:hidden"
    >
      <NavLink to="/my-work" className={cls} data-tour="nav-my-work">
        <ListTodo className="size-5" aria-hidden />
        Моя робота
      </NavLink>
      <NavLink to="/inbox" className={cls} data-tour="nav-inbox">
        <span className="relative">
          <Inbox className="size-5" aria-hidden />
          {!!unread.data && <Counter value={unread.data} tone="accent" className="absolute -top-1.5 left-3 h-4 min-w-4 px-1 text-[10px]" label={`Непрочитаних: ${unread.data}`} />}
        </span>
        Вхідні
      </NavLink>
      <button type="button" onClick={onCreate} className={cn(item, 'text-fg-2')} aria-label="Нова задача" data-tour="topbar-new-task">
        <span className="flex size-9 items-center justify-center rounded-md bg-accent text-on-accent">
          <Plus className="size-5" aria-hidden />
        </span>
      </button>
      <NavLink to="/projects" className={cls}>
        <FolderKanban className="size-5" aria-hidden />
        Проєкти
      </NavLink>
      <NavLink to="/search" className={cls}>
        <Search className="size-5" aria-hidden />
        Пошук
      </NavLink>
    </nav>
  );
}

/** Оболонка застосунку: навігація, верхня панель, картка задачі, швидке створення, панель команд, гарячі клавіші. */
export function AppShell() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { userId } = useSession();
  const desktop = useIsDesktop();
  const online = useOnline();
  const [navOpen, setNavOpen] = useState(false);
  const [create, setCreate] = useState<{ open: boolean; defaults: CreateDefaults }>({ open: false, defaults: {} });
  const [palette, setPalette] = useState(false);
  const [shortcuts, setShortcuts] = useState(false);
  // Ліву панель можна згорнути (як в Asana); стан пам'ятається в браузері.
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage<boolean>('ers.sidebar.collapsed', false);
  const collapsed = desktop && sidebarCollapsed;
  // Розгорнута панель — завширшки за найдовшою назвою розділу (вимірюємо вміст), згорнута — смуга значків 56 px.
  const sidebarRef = useRef<HTMLElement>(null);
  const [sidebarW, setSidebarW] = useState(232);
  useLayoutEffect(() => {
    const el = sidebarRef.current;
    if (!el || collapsed || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const w = Math.ceil(el.getBoundingClientRect().width);
      if (w > 0) setSidebarW(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed]);
  const searchRef = useRef<HTMLInputElement>(null);
  const taskId = params.get('task');

  useRealtime(true);

  useEffect(() => setNavOpen(false), [location.pathname]);
  useEffect(() => {
    if (desktop) setNavOpen(false);
  }, [desktop]);

  const openTask = useCallback(
    (id: string) => {
      if (location.pathname.startsWith('/tasks/')) {
        navigate(`/tasks/${id}`);
        return;
      }
      const next = new URLSearchParams(params);
      const had = next.has('task');
      next.set('task', id);
      setParams(next, { replace: had, state: { taskPanel: true } });
    },
    [location.pathname, navigate, params, setParams],
  );

  const closeTask = useCallback(() => {
    if ((location.state as { taskPanel?: boolean } | null)?.taskPanel) {
      navigate(-1);
      return;
    }
    const next = new URLSearchParams(params);
    next.delete('task');
    setParams(next, { replace: true });
  }, [location.state, navigate, params, setParams]);

  const actions = useMemo<ShellActions>(
    () => ({
      openCreateTask: (defaults = {}) => setCreate({ open: true, defaults }),
      openTask,
      closeTask,
      openPalette: () => setPalette(true),
      openShortcuts: () => setShortcuts(true),
      focusSearch: () => {
        if (searchRef.current && searchRef.current.offsetParent !== null) searchRef.current.focus();
        else navigate('/search');
      },
    }),
    [openTask, closeTask, navigate],
  );

  // Гарячі клавіші. Коди клавіш (KeyQ, Slash), а не символи — працюють і в українській розкладці.
  // Tab+Q (як в Asana) — швидка задача навіть із поля введення: Q, поки утримується Tab.
  useEffect(() => {
    let tabDown = false;
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Tab') tabDown = false;
    };
    const onBlur = () => {
      tabDown = false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && !e.repeat) tabDown = true;
      if (e.defaultPrevented || e.isComposing) return;
      if (tabDown && e.code === 'KeyQ' && !document.querySelector('dialog[open]')) {
        e.preventDefault();
        tabDown = false;
        actions.openCreateTask();
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && !e.altKey && e.code === 'KeyK') {
        e.preventDefault();
        setPalette((p) => !p);
        return;
      }
      if (mod || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('dialog[open]')) return;
      if (e.code === 'KeyQ' && !e.shiftKey) {
        e.preventDefault();
        actions.openCreateTask();
      } else if (e.code === 'Slash' && e.shiftKey) {
        e.preventDefault();
        setShortcuts(true);
      } else if (e.code === 'Slash' || e.key === '/') {
        e.preventDefault();
        actions.focusSearch();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [actions]);

  // Події встановлюваного застосунку: офлайн-черга, відправлення збережених дій, перехід зі сповіщення.
  const qc = useQueryClient();
  useEffect(
    () =>
      subscribePwa((m) => {
        if (m.type === 'ers:queued') toast.info('Немає мережі — дію збережено', 'Її буде надіслано автоматично, щойно з’явиться зв’язок.');
        else if (m.type === 'ers:replayed') {
          if (m.ok) toast.success('Надіслано дію, збережену офлайн');
          else toast.error('Збережену офлайн дію не прийнято', m.detail || undefined);
          void qc.invalidateQueries();
        } else if (m.type === 'ers:navigate') navigate(m.url);
        else if (m.type === 'ers:sw-activated') {
          // На сервері вже нова версія, а ця вкладка працює на попередній: пропонуємо оновити, не перериваючи роботу.
          if (m.version !== BUILD_ID && m.version !== 'dev') toast.info('Вийшла нова версія застосунку', 'Оновіть сторінку, щойно завершите поточну дію.', { label: 'Оновити', onClick: () => window.location.reload() });
        } else if (m.type === 'ers:dropped') {
          const why = m.reason === 'expired' ? 'Вони чекали мережі понад 7 днів.' : m.reason === 'signed-out' ? 'Після виходу черга очищується.' : 'Їх створено під іншим обліковим записом або до попереднього виходу.';
          toast.info(`Відкладені дії не надіслано: ${m.count}`, why);
        } else if (m.type === 'ers:identity') {
          // В іншій вкладці вийшли або ввійшли під іншим обліковим записом: ця вкладка не має показувати чужі дані.
          if (m.state === 'signed-out') endSessionInPage(qc);
          else if (m.state === 'signed-in' && m.userId !== userId) {
            // Стан екранів попереднього працівника (чернетки, останні проєкти) новому не дістається.
            clearUserLocalState();
            window.location.reload();
          }
        }
      }),
    [toast, qc, navigate, userId],
  );
  const offlineStamp = useSyncExternalStore(subscribeOfflineStamp, getOfflineStamp);
  // Збережені копії звужені: без фінансових і управлінських даних, а /me — з меншим набором прав. Щойно сервер знову
  // відповідає сам, усе перечитуємо повним: інакше після короткого збою мережі розділи лишалися б прихованими.
  const servedOffline = useRef(false);
  useEffect(() => {
    if (!offlineStamp && servedOffline.current) void qc.invalidateQueries();
    servedOffline.current = !!offlineStamp;
  }, [offlineStamp, qc]);

  // Фокус перейшов на інший рядок (↑/↓ у переліку) — відкрита картка слідує за ним.
  useEffect(() => {
    if (!taskId) return;
    const onFocusTask = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (id && id !== taskId) openTask(id);
    };
    window.addEventListener(TASK_FOCUS_EVENT, onFocusTask);
    return () => window.removeEventListener(TASK_FOCUS_EVENT, onFocusTask);
  }, [taskId, openTask]);

  const onCreated = (task: Task) => {
    toast.success(`Задачу ${task.key} створено`, task.title, { label: 'Відкрити картку', onClick: () => openTask(task.id) });
  };

  return (
    <ShellContext.Provider value={actions}>
      <TaskActionsProvider onOpenTask={openTask}>
        <TransitionGuardProvider>
          <TrainingProvider>
            {/* Ширина картки задачі: на широкому екрані картка звужує основну область (як в Asana), а не накриває переліки. */}
            <div
              className="min-h-dvh bg-canvas [--task-panel-w:min(max(560px,50vw),calc(100vw-var(--sidebar-w)-160px))]"
              style={{ '--sidebar-w': collapsed ? '56px' : `${sidebarW}px` } as CSSProperties}
            >
              <a
                href="#app-main"
                className="sr-only fixed top-2 left-2 z-[90] rounded-sm bg-accent px-3 py-2 text-body font-medium text-on-accent focus:not-sr-only"
              >
                Перейти до вмісту
              </a>
              <aside
                id="app-sidebar"
                ref={sidebarRef}
                className={cn(
                  'ers-chrome fixed inset-y-0 left-0 z-20 hidden border-r border-line bg-canvas lg:block',
                  collapsed ? 'w-14' : 'w-max min-w-[184px] max-w-[280px]',
                )}
              >
                <SidebarNav collapsed={collapsed} />
              </aside>
              <div className="flex min-h-dvh flex-col lg:pl-[var(--sidebar-w)]">
                <div id="app-topbar" className="sticky top-0 z-30">
                  <Topbar
                    onOpenNav={() => setNavOpen(true)}
                    searchRef={searchRef}
                    sidebarCollapsed={collapsed}
                    onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                  />
                  {(!online || offlineStamp) && (
                    <div role="status" className="flex items-center gap-2 border-b border-warning-line bg-warning-soft px-4 py-1.5 text-meta text-warning-fg">
                      <WifiOff className="size-4 shrink-0" aria-hidden />
                      <span>
                        {offlineStamp ? `Офлайн — дані на ${formatDateTime(offlineStamp)}.` : 'Немає з’єднання з мережею.'} Таймер, коментарі й позначку «виконано» буде надіслано,
                        щойно з’явиться зв’язок.
                      </span>
                    </div>
                  )}
                </div>
                <main
                  id="app-main"
                  tabIndex={-1}
                  className={cn(
                    'flex min-h-0 flex-1 flex-col bg-surface pb-[calc(56px+env(safe-area-inset-bottom))] outline-none sm:pb-0',
                    taskId && 'lg:pr-[var(--task-panel-w)]',
                  )}
                >
                  <Outlet />
                </main>
              </div>
              <BottomNav onCreate={() => actions.openCreateTask()} />
              <NavDrawer open={navOpen} onClose={() => setNavOpen(false)} />
              {taskId && <TaskPanel taskId={taskId} onClose={closeTask} onNavigate={openTask} />}
              <CreateTaskDialog open={create.open} defaults={create.defaults} onClose={() => setCreate((c) => ({ ...c, open: false }))} onCreated={onCreated} />
              <CommandPalette open={palette} onClose={() => setPalette(false)} />
              <ShortcutsDialog open={shortcuts} onClose={() => setShortcuts(false)} />
            </div>
          </TrainingProvider>
        </TransitionGuardProvider>
      </TaskActionsProvider>
    </ShellContext.Provider>
  );
}
